import get from 'lodash-es/get.js';
import { CONFIG } from '../config.js';
import { extractDomain } from '../utils/url.js';

/**
 * Normalizzazione della risposta grezza di Hacker News.
 *
 * L'API restituisce oggetti eterogenei (story, job, poll, item cancellati,
 * addirittura `null` se l'ID non esiste più) e con campi opzionali.
 * Qui la risposta viene tradotta in un modello interno stabile, così che la UI
 * non debba mai fare controlli difensivi sui dati.
 */

/**
 * Converte un item grezzo dell'API nel modello usato dall'applicazione.
 *
 * @param {object|null|undefined} raw item restituito da /item/<id>.json
 * @returns {{
 *   id: number, title: string, url: string, hnUrl: string, domain: string,
 *   author: string, score: number, comments: number, time: number, type: string,
 *   isExternal: boolean
 * } | null} `null` se l'item non è mostrabile
 */
export function normalizeStory(raw) {
  const id = get(raw, 'id');
  if (!raw || typeof id !== 'number') return null;

  // Item cancellati o moderati non vanno mostrati.
  if (get(raw, 'deleted', false) || get(raw, 'dead', false)) return null;

  const title = String(get(raw, 'title', '')).trim();
  if (!title) return null;

  const externalUrl = get(raw, 'url', '');
  const hnUrl = `${CONFIG.HN_ITEM_URL}${id}`;

  return {
    id,
    title,
    url: externalUrl || hnUrl,
    hnUrl,
    domain: externalUrl ? extractDomain(externalUrl) : 'news.ycombinator.com',
    author: get(raw, 'by', 'anonimo'),
    score: Number(get(raw, 'score', 0)) || 0,
    comments: Number(get(raw, 'descendants', 0)) || 0,
    time: Number(get(raw, 'time', 0)) || 0,
    type: get(raw, 'type', 'story'),
    isExternal: Boolean(externalUrl),
  };
}

/**
 * Normalizza una collezione di item scartando quelli non mostrabili.
 *
 * @param {Array<object|null>} rawItems
 * @returns {Array<object>}
 */
export function normalizeStories(rawItems = []) {
  return rawItems.map(normalizeStory).filter(Boolean);
}
